import { Feedback, HighlightsSummary } from "../types";
import { HugeiconsIcon } from "@hugeicons/react";
import { InformationCircleIcon } from "@hugeicons-pro/core-solid-rounded";
import { useEffect, useMemo, useState } from "react";
import { FeedbackButtons } from "../components/FeedbackButtons";
import { parseAIContent } from "../helpers";
import { Applicant } from "types/application";

// In milliseconds
const LETTER_DELAY = 7;
const NEXT_LINE_DELAY = 20;

const BulletPoints = ({ importantPoints }: { importantPoints: string[] }) => {
  const [typedItems, setTypedItems] = useState<string[]>([]);
  const [currentLetters, setCurrentLetters] = useState("");
  const [exampleIndex, setExampleIndex] = useState(0);

  useEffect(() => {
    if (exampleIndex >= importantPoints.length) return;

    const point = importantPoints[exampleIndex];
    if (currentLetters.length < point.length) {
      const timeout = setTimeout(() => {
        setCurrentLetters(point.slice(0, currentLetters.length + 1));
      }, LETTER_DELAY);
      return () => clearTimeout(timeout);
    } else {
      // 20ms pause before moving to the next point
      const timeout = setTimeout(() => {
        setTypedItems((prev) => [...prev, point]);
        setCurrentLetters("");
        setExampleIndex((prev) => prev + 1);
      }, NEXT_LINE_DELAY);
      return () => clearTimeout(timeout);
    }
  }, [currentLetters, exampleIndex, importantPoints]);

  return (
    <ul className="ai_star-list w-full p-6 space-y-2">
      {typedItems.map((ex, i) => (
        <li className="text-sm font-medium text-content-400" key={`${ex}_${i}`}>
          {ex}
        </li>
      ))}

      {currentLetters ? (
        // color is lighter intentionally to show it's still typing
        <li className="text-sm font-medium text-content-200">
          {currentLetters}
        </li>
      ) : null}
    </ul>
  );
};

const NextSteps = ({
  recommendedNextStep,
  recommendedNextStepTitle
}: {
  recommendedNextStep: string;
  recommendedNextStepTitle: string;
}) => {
  const [typedTitle, setTypedTitle] = useState("");
  const [typedStep, setTypedStep] = useState("");
  const [titleComplete, setTitleComplete] = useState(false);

  useEffect(() => {
    // Type the title first
    if (typedTitle.length < recommendedNextStepTitle.length) {
      const timeout = setTimeout(() => {
        setTypedTitle(recommendedNextStepTitle.slice(0, typedTitle.length + 1));
      }, LETTER_DELAY);
      return () => clearTimeout(timeout);
    } else if (!titleComplete) {
      // Small pause before starting the next step
      const timeout = setTimeout(() => {
        setTitleComplete(true);
      }, NEXT_LINE_DELAY);
      return () => clearTimeout(timeout);
    }
  }, [typedTitle, recommendedNextStepTitle, titleComplete]);

  useEffect(() => {
    // Type the step after title is complete
    if (titleComplete && typedStep.length < recommendedNextStep.length) {
      const timeout = setTimeout(() => {
        setTypedStep(recommendedNextStep.slice(0, typedStep.length + 1));
      }, LETTER_DELAY);
      return () => clearTimeout(timeout);
    }
  }, [typedStep, recommendedNextStep, titleComplete]);

  return (
    <div className="w-full p-6">
      <div className="bg-[#FFF7FB] h-full pl-2 pr-8 py-5 flex items-center rounded-md min-h-[172px] relative overflow-hidden animate-appear">
        <div className="ripple outer-circle" />
        <div className="ripple middle-circle" />
        <div className="ripple inner-circle" />

        <div className="relative z-10 shrink-0">
          <img
            src="/assets/ai_star_large.svg"
            className="breathe-img"
            height={106}
            loading="lazy"
            width={106}
            alt="ai recommendation"
          />
        </div>

        <div>
          <h1 className="text-base font-bold text-primary-700 mb-2">
            {typedTitle}
          </h1>
          <p className="text-sm font-medium text-primary-700">{typedStep}</p>
        </div>
      </div>
    </div>
  );
};

export const ApplicationIntelligenceSuccess = (
  props: HighlightsSummary & {
    feedback?: Feedback;
    applicationId: number;
    applicants: Applicant[];
  }
) => {
  // Parse AI content to replace applicant tags with actual names
  const parsedImportantPoints = useMemo(
    () =>
      props.ai_response.important_points.map((point) =>
        parseAIContent(point, props.applicants)
      ),
    [props.ai_response.important_points, props.applicants]
  );

  const parsedNextStep = useMemo(
    () =>
      parseAIContent(props.ai_response.recommended_next_step, props.applicants),
    [props.ai_response.recommended_next_step, props.applicants]
  );

  const parsedNextStepTitle = useMemo(
    () =>
      parseAIContent(
        props.ai_response.recommended_next_step_short,
        props.applicants
      ),
    [props.ai_response.recommended_next_step_short, props.applicants]
  );

  return (
    <>
      <section className="grid grid-cols-1 gap-x-2 xl:grid-cols-2 xl:divide-x divide-alpha-40">
        <BulletPoints importantPoints={parsedImportantPoints} />

        <NextSteps
          recommendedNextStep={parsedNextStep}
          recommendedNextStepTitle={parsedNextStepTitle}
        />
      </section>

      <footer className="border-t border-alpha-40 px-9 flex justify-between items-center h-[72px]">
        <div className="flex gap-2 items-center">
          <HugeiconsIcon
            icon={InformationCircleIcon}
            className="text-content-100"
            size={16}
          />
          <p className="text-content-200 font-medium text-sm">
            Generated by AI. AI can make mistakes. Review application details
          </p>
        </div>
        <FeedbackButtons
          summaryId={props.id}
          applicationId={props.applicationId}
          feedback={props.feedback}
        />
      </footer>
    </>
  );
};
